import type { TicketJira } from '@vd/types';

/** What one send of tickets to Jira did, as the server counts it (TOADO-708). */
export type SentToJira = { queued: number; alreadyInJira: number; alreadySending: number; failed: number; notFound: number };

/** The most tickets the server takes in one send (`MAX_BULK_SEND` in the API's routes/jira.ts). */
export const JIRA_SEND_BATCH = 100;
/** How many times one batch waits out the server's rate limit before it counts as failed. */
const RATE_LIMIT_RETRIES = 3;

/**
 * Sends a selection of any size, `JIRA_SEND_BATCH` at a time, adding up what
 * each batch did (Codex review round 1: a table's Select all can pass 100). A
 * batch that fails leaves it and the ones after unsent, counted as `failed`,
 * so the toast never reports less than what went.
 */
export async function sendInBatches(ticketIds: string[], send: (batch: string[]) => Promise<SentToJira>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms))): Promise<SentToJira> {
  const total: SentToJira = { queued: 0, alreadyInJira: 0, alreadySending: 0, failed: 0, notFound: 0 };
  // A batch the server turned away for coming too fast waits as it says and
  // goes again, a few times, rather than counting the rest as failed (Codex
  // review round 2: a very large selection outpaces the route's limit).
  const sendPaced = async (batch: string[]): Promise<SentToJira> => {
    for (let tries = 0; ; tries += 1) {
      try {
        return await send(batch);
      } catch (error) {
        const { status, retryAfterMs } = error as { status?: number; retryAfterMs?: number };
        if (status !== 429 || tries >= RATE_LIMIT_RETRIES) throw error;
        await wait(Math.min(retryAfterMs ?? 5_000, 60_000));
      }
    }
  };
  for (let start = 0; start < ticketIds.length; start += JIRA_SEND_BATCH) {
    try {
      const counts = await sendPaced(ticketIds.slice(start, start + JIRA_SEND_BATCH));
      for (const key of Object.keys(total) as Array<keyof SentToJira>) total[key] += counts[key] ?? 0;
    } catch (error) {
      // Nothing went at all: the caller says why, as for any failed request.
      if (start === 0) throw error;
      total.failed += ticketIds.length - start;
      break;
    }
  }
  return total;
}

const tickets = (n: number) => `${n} ticket${n === 1 ? '' : 's'}`;

/**
 * The toast for a send: what is on its way, and what was left alone or did
 * not go, and why. Nothing sent, or anything that failed, is a warning, so it
 * is not taken for a success.
 */
export function sentToJiraSummary(counts: SentToJira): { variant: 'success' | 'warning'; title: string; description?: string } {
  const left: string[] = [];
  if (counts.alreadyInJira) left.push(`${counts.alreadyInJira} already in Jira`);
  if (counts.alreadySending) left.push(`${counts.alreadySending} already on the way`);
  if (counts.notFound) left.push(`${counts.notFound} no longer here`);
  const parts = [
    ...(counts.failed ? [`${tickets(counts.failed)} could not be sent; try again.`] : []),
    ...(left.length ? [`Left alone: ${left.join(', ')}.`] : []),
  ];
  const description = parts.length ? parts.join(' ') : undefined;
  if (counts.queued === 0) return { variant: 'warning', title: counts.failed ? 'Could not send to Jira' : 'Nothing new to send to Jira', description };
  return { variant: counts.failed ? 'warning' : 'success', title: `Sending ${tickets(counts.queued)} to Jira`, description };
}

/**
 * Whether a Jira state heard live replaces the one a view holds (TOADO-708):
 * not when it is older. Announcements can arrive out of order (one queued
 * just before its ticket finished), and each carries the ticket's version.
 */
export function laterJira(held: TicketJira | undefined, heard: TicketJira): boolean {
  return !held || heard.revision >= held.revision;
}

/**
 * A view's memory of the newest Jira word it applied per ticket, kept after a
 * link is cleared: an unlink leaves the card with no Jira state to compare
 * against, so without it an older "in Jira" heard next would put the issue
 * back (z.ai and Codex reviews of the unlink). Answers whether a word heard
 * now, a state or its removal, is to be applied. A word with no revision (from
 * a server before revisions rode with a removal) is applied as before.
 */
export function jiraWatermark() {
  const applied = new Map<string, number>();
  return (ticketId: string, held: TicketJira | undefined, heard: TicketJira | null, heardRevision?: number): boolean => {
    const revision = heard?.revision ?? heardRevision;
    if (revision === undefined) return heard === null || laterJira(held, heard);
    const known = Math.max(held?.revision ?? Number.NEGATIVE_INFINITY, applied.get(ticketId) ?? Number.NEGATIVE_INFINITY);
    if (revision < known) return false;
    applied.set(ticketId, revision);
    return true;
  };
}

/** The most cards a bulk send takes without asking first (TOADO-849). */
export const BIG_SEND = 10;

/**
 * What a bulk send says before it goes, or null when it just goes (TOADO-849):
 * asked when it is over `BIG_SEND` cards or its cards come from more than one
 * column. A select-all on a board sent 102 cards when only the To do ones were
 * meant. Columns are named in board order, each with its count.
 */
export function bigSendCheck(
  tickets: ReadonlyArray<{ columnId: string }>,
  columns: ReadonlyArray<{ id: string; name: string; order: number }>,
): { title: string; description: string } | null {
  const counts = new Map<string, number>();
  for (const ticket of tickets) counts.set(ticket.columnId, (counts.get(ticket.columnId) ?? 0) + 1);
  if (tickets.length <= BIG_SEND && counts.size <= 1) return null;
  const named = [...columns].sort((left, right) => left.order - right.order || (left.id < right.id ? -1 : left.id > right.id ? 1 : 0))
    .filter((column) => counts.has(column.id))
    .map((column) => `${column.name} (${counts.get(column.id)})`);
  const unknown = tickets.length - [...columns].reduce((total, column) => total + (counts.get(column.id) ?? 0), 0);
  if (unknown > 0) named.push(`other columns (${unknown})`);
  const list = named.length > 1 ? `${named.slice(0, -1).join(', ')} and ${named.at(-1)}` : named[0] ?? '';
  return {
    title: `Send ${tickets.length} ${tickets.length === 1 ? 'ticket' : 'tickets'} to Jira?`,
    description: `${list ? `From ${list}. ` : ''}Toado makes one Jira issue for each, and Jira keeps them if you unlink.`,
  };
}

/** What a bulk unlink says once it is done (TOADO-849). */
export function unlinkedSummary(ok: number, failed: number): { variant: 'success' | 'warning'; title: string; description: string } {
  return {
    variant: failed === 0 ? 'success' : 'warning',
    title: failed === 0 ? `Unlinked ${ok} ${ok === 1 ? 'ticket' : 'tickets'} from Jira` : `Unlinked ${ok}, ${failed} could not be`,
    description: 'Jira is not changed. Sending again links an issue back while it is still in Jira, or makes a new one if it was deleted.',
  };
}
